// Marine Regions, queried by NAME — the search's second tier.
//
// forecast/primer.ts already asks this service the reverse question ("what is
// under 27.5 N 94.2 W") to name the seabed feature and the water body at a point.
// This asks it forwards, which is what turns a typed word into a place: seas,
// undersea features and maritime zones, none of which the local index holds.
//
// A COUNTRY RESOLVES TO ITS EEZ, NEVER TO THE COUNTRY. Searching "Angola" returns
// a Nation record at 11.2 S 17.9 E, which is 400 km inland in the middle of the
// Angolan highlands. Sending a marine console there produces no wind, no sea and
// no field — a result that is technically correct and operationally useless. The
// same query also returns the Angolan Exclusive Economic Zone at 11.7 S 11.0 E,
// which is water, and water is what a survey company means by "Angola". So Nation
// records are dropped outright and EEZ records carry the country.
//
// MOST OF WHAT COMES BACK IS NOISE, AND IT IS THE SAME PLACE. "Black Sea" returns
// 33 records: an IHO Sea Area, a Large Marine Ecosystem, a Marine Province, a MEOW
// ecoregion, an ICES ecoregion, a SeaVoX sub-region and more, all describing one
// body of water under different scientific classifications. Showing them is
// showing a taxonomy, not a search result. Hence the allowlist below, and the
// dedupe by name that follows it: one row per place.
//
// SOURCES. IHO-IOC GEBCO Gazetteer of Undersea Feature Names and the US BGN's
// ACUF for seabed features; IHO Sea Areas and SeaVoX for water bodies; Marine
// Regions' own EEZ layer for maritime zones. Attribution is carried on the map's
// attribution line already, which is where it belongs; licence review for the
// paid product is open work (task #34).

/** What the console can do with a gazetteer hit. */
export type GazKind = "sea" | "feature" | "zone";

export interface GazPlace {
  kind: GazKind;
  name: string;
  /** The record's own classification — "Abyssal Plain", "IHO Sea Area", "EEZ". */
  placeType: string;
  lat: number;
  lon: number;
  /** Widest side of the record's bounding box in nm, when it declares one. This
   *  is what lets "Black Sea" open framed on the Black Sea instead of at whatever
   *  width the map happened to be at. */
  spanNm?: number;
}

const BASE = "https://www.marineregions.org/rest/getGazetteerRecordsByName.json/";

/** Classifications worth showing, and what each becomes.
 *
 *  Deliberately an ALLOWLIST. A blocklist would let every new classification the
 *  service adds appear in front of a client as a duplicate row. */
const PLACE_TYPES: Record<string, GazKind> = {
  EEZ: "zone",
  "IHO Sea Area": "sea",
  "SeaVoX SeaArea - sub-region": "sea",
  "SeaVoX SeaArea - region": "sea",
  Gulf: "sea",
  Bay: "sea",
  Strait: "sea",
  Channel: "sea",
  Sound: "sea",
  Fjord: "sea",
  Lagoon: "sea",
};

/** Sources whose records are seabed features whatever their placeType — the
 *  placeType there is the feature's own term ("Abyssal Plain", "Escarpment",
 *  "Seamount"), which is far too long a list to enumerate. */
const FEATURE_SOURCES = ["GEBCO", "ACUF", "Undersea Feature"];

/** Seabed feature terms accepted from ANY source.
 *
 *  The source allowlist above misses records that are unambiguously features but
 *  are catalogued elsewhere — Campos Basin, which is where half of Brazil's
 *  offshore work happens, comes from Aphia and was being dropped. Judging these
 *  by what they ARE rather than by who filed them is the more robust test, and a
 *  basin is a basin whoever wrote it down. */
const FEATURE_TYPES = new Set([
  "Abyssal Plain",
  "Bank",
  "Basin",
  "Canyon",
  "Deep",
  "Escarpment",
  "Fan",
  "Fracture Zone",
  "Guyot",
  "Hill(s)",
  "Knoll",
  "Knoll(s)",
  "Mound(s)",
  "Plateau",
  "Reef",
  "Ridge",
  "Rise",
  "Seamount",
  "Seamount(s)",
  "Shelf",
  "Shoal",
  "Sill",
  "Slope",
  "Spur",
  "Terrace",
  "Trench",
  "Trough",
  "Valley",
]);

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null ? (v as Record<string, unknown>) : null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function kindOf(placeType: string, source: string): GazKind | null {
  if (placeType === "Nation") return null; // inland centroid — see the header note
  const byType = PLACE_TYPES[placeType];
  if (byType !== undefined) return byType;
  if (FEATURE_TYPES.has(placeType)) return "feature";
  if (FEATURE_SOURCES.some((s) => source.includes(s))) return "feature";
  return null;
}

/** The widest side of a declared bounding box, in nm. */
function bboxNm(r: Record<string, unknown>, lat: number): number | undefined {
  const y0 = num(r.minLatitude);
  const y1 = num(r.maxLatitude);
  const x0 = num(r.minLongitude);
  const x1 = num(r.maxLongitude);
  if (y0 === null || y1 === null || x0 === null || x1 === null) return undefined;
  const hNm = Math.abs(y1 - y0) * 60;
  const wNm = Math.abs(x1 - x0) * 60 * Math.cos((lat * Math.PI) / 180);
  const span = Math.max(hNm, wNm);
  return span > 0 ? Math.round(span) : undefined;
}

/**
 * Look a name up. Returns [] for anything that fails — an unreachable gazetteer
 * must degrade to "the local results only", never to a broken search box.
 *
 * `signal` is the caller's abort: every keystroke supersedes the one before it,
 * and a reply that arrives after the query moved on is worse than no reply.
 */
export async function gazetteerSearch(q: string, signal?: AbortSignal): Promise<GazPlace[]> {
  const term = q.trim();
  if (term.length < 3) return [];
  // like=true does the substring match; fuzzy=false because fuzzy on a short
  // term returns half the ocean and buries the exact hit.
  const url = `${BASE}${encodeURIComponent(term)}/true/false/`;
  let rows: unknown;
  try {
    const res = await fetch(url, { signal });
    // 404 is this API's "no matches", not a failure.
    if (res.status === 404) return [];
    if (!res.ok) throw new Error(`Marine Regions HTTP ${res.status}`);
    rows = (await res.json()) as unknown;
  } catch (err) {
    if ((err as { name?: string } | null)?.name === "AbortError") return [];
    console.warn("Gazetteer search unavailable:", err);
    throw err;
  }
  if (!Array.isArray(rows)) return [];

  // English first, then everything else, and the dedupe below keeps whichever it
  // meets first. The gazetteer carries every language a feature has been named
  // in: "Angola Basin" also arrives as Angola Nogu, Angola-Becken and
  // Angolabackenet, which is four rows of an eight-row list spent on one place
  // in three languages nobody on this bridge is reading.
  const records = (rows as unknown[])
    .map(asRecord)
    .filter((r): r is Record<string, unknown> => r !== null);
  const english = (r: Record<string, unknown>): boolean => {
    const lang = str(r.preferredGazetteerNameLang);
    return lang === "" || lang === "English";
  };
  const ordered = [...records.filter(english), ...records.filter((r) => !english(r))];

  const out: GazPlace[] = [];
  const seen = new Set<number>();
  for (const r of ordered) {
    const name = str(r.preferredGazetteerName);
    const placeType = str(r.placeType);
    const source = str(r.gazetteerSource);
    const lat = num(r.latitude);
    const lon = num(r.longitude);
    if (name === "" || lat === null || lon === null) continue;
    const kind = kindOf(placeType, source);
    if (kind === null) continue;
    // ONE ROW PER PLACE, keyed on the gazetteer's own identity rather than on the
    // text. Alternative names for one feature share an MRGID — "Sigsbee Abyssal
    // Plain" and "Sigsbee Plain" are both 25050 — and a synonym points at its
    // accepted record through `accepted`. Deduping on the name instead would
    // keep every synonym; deduping on position would merge features that
    // genuinely share a centroid. The identifier is the thing that means "same
    // place", so it is the thing to key on.
    const id = num(r.accepted) ?? num(r.MRGID);
    if (id !== null) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    const spanNm = bboxNm(r, lat);
    out.push({ kind, name, placeType, lat, lon, ...(spanNm === undefined ? {} : { spanNm }) });
  }
  return out;
}
